/**
 * 레벨 공용 정의 — 방·벽·건물·조사물·잠금·라운드·감정·조각 배치의 **타입과 상수.**
 *
 * 에피소드마다 다른 것은 데이터(`level-<ep>.ts`, `.place.ts`, `.bundle.ts`)이고,
 * 모든 에피소드가 같이 쓰는 모양은 여기 있다. 원래 사흘 v3 레벨 파일
 * (`level-sahol.ts`, `level-sahol.furniture.ts`)이 타입까지 들고 있었는데,
 * v3 를 지우면서(2026-09-23) 정의만 이리로 옮겼다.
 */

import type { ObjState } from './protocol';

/**
 * 천장 높이. **3.2m 였다** — 눈높이 1.62m 인 사람 머리 위에 제 눈높이만큼(1.58m)이 그대로
 * 비어서 어느 방이든 체육관으로 읽혔다(2026-09-11, Chris: 「다 너무 뭐가 없는거 아님?」).
 * 가구를 더 넣어서 될 일이 아니었다 — 부피가 문제였다.
 *
 *   천장 3.20  머리 위 1.58m (눈높이의 0.98배)   ← 예전
 *   천장 2.60  머리 위 0.98m (0.60배)            ← 지금. 공공·상가 실제값(2.6~2.8)
 *   천장 2.40  머리 위 0.78m (0.48배)            시골 살림집 실제값이지만 여기선 못 쓴다
 *
 * **2.4 로 더 못 내리는 이유**: 문 상단 `DOOR_HEAD` 가 2.2 라 문 위 벽이 0.26 밖에 안 남고,
 * 거기서 몰딩 0.09 를 빼면 0.11 — 문틀이 천장 몰딩에 닿는다. 더 낮추려면 문·창 높이를 먼저 내려야 한다.
 *
 * `architecture.ts` 는 처마·서까래·홈통을 전부 이 값의 **상대값**으로 잡으므로 외관이 같이 따라온다.
 */
export const WALL_H = 2.6;

export const WALL_T = 0.3;

export const PLAYER_R = 0.34;

export const EYE_H = 1.62;

export interface RoomDef {
  id: string;
  name: string;
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  light: string;
  /**
   * **벽 마감 세트** (2026-09-14, Chris: 「벽지 색상이나 재질 고르는 게 어렵다 — 지능적으로」).
   *
   * 색을 낱낱이 고르게 하지 않는다. 방이 42개인데 그중 24개가 관사 6채의 반복이라,
   * 자유 색상표를 열면 여섯 채가 제각각이 되어 오히려 무너진다. **「이 방이 무엇인가」만**
   * 고르면 벽·징두리·몰딩이 한 벌로 딸려온다.
   *
   * `school` — 고친 분교. 바랜 세이지 벽 + 나무 징두리 1.05m + 얇은 몰딩.
   *   촌스러워지는 건 언제나 **채도**다. 하늘색으로 칠하면 유치원이 된다.
   */
  finish?: string;   // FinishSetId (shared/finishes.ts)
  /** 세트와 별개로 벽·징두리만 따로 고른 것 (2026-09-14, Chris: 「벽 세트 / 벽지만 / 바닥만」). 세트보다 우선 */
  wall?: string;
  wains?: string | null;   // null = 징두리 없이 벽 하나로
  /**
   * 창 가리개 (2026-09-15, Chris: 「커튼이나 블라인드가 필요하지 않으려나」). 조각으로 놓으면
   * 창 앞 규칙에 걸리고 창을 옮기면 남는다 — 벽 생성기가 이 방의 창마다 실내 쪽에 구워 넣는다.
   * `open` 0 = 닫힘, 1 = 활짝(기본 0.7). 색은 사람마다 — 사진가는 짙게, 도예가는 얇게.
   */
  dress?: { kind: 'blind' | 'curtain'; color?: string; open?: number };
  /**
   * 방 바닥 높이. 지정하지 않으면 0.
   *
   * 오르막 위에 앉은 집을 위해 있다. 바닥·천장·등이 전부 이 값을 기준으로 올라간다 —
   * 하나라도 빠뜨리면 집은 언덕 위에 있는데 등만 땅에 박힌다.
   */
  y?: number;
  /** 조명 기구. 폐교와 고친 빈집이라 전부 형광등이다 */
  fixture?: 'pendant' | 'fluorescent';
  /** 바닥 재질. 지정하지 않으면 마루 */
  floor?: string;   // SurfaceId (client/src/materials.ts 카탈로그)
}

export interface WallDef {
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  y?: number;
  h?: number;
  /**
   * 이 토막이 **개구부**라는 표시. 문 위 인방이면 `'door'`, 창 아래·위 토막이면
   * `'window'`. 벽을 세우는 데는 쓰지 않고 **시선 판정**이 쓴다 —
   * 문간으로는 손이 닿아야 하고 유리창 너머로는 안 닿아야 한다.
   */
  gap?: 'door' | 'window';
  /** 이 벽이 선 바닥 높이(언덕 위 집 3.6). 없으면 0. world.ts 가 몰딩·충돌체 판단에 쓴다 */
  base?: number;
  /** 어느 wallRun 이 낳았나 — 배치 에디터가 창·문을 옮기면 그 run 만 다시 짓는다 */
  run?: string;
}

export interface FurnDef {
  shape: 'box' | 'cyl';
  pos: [number, number, number];
  size: [number, number, number];
  rot?: number;
  color: string;
  solid?: boolean;
  rough?: number;
  metal?: number;
  surface?: 'oak' | 'darkwood' | 'tile' | 'concrete' | 'plaster' | 'photowall';
}

export type PropKind = 'door' | 'drawer' | 'pickup' | 'switch' | 'exam' | 'safe' | 'board' | 'lid';

export interface PropDef {
  id: string;
  kind: PropKind;
  name: string;
  /** 구역 id. 야외는 'outside' */
  room: string;
  pos: [number, number, number];
  rot?: number;
  size?: [number, number, number];
  color?: string;
  init?: ObjState;
  /** 솔버가 카탈로그 openable 에서 자동으로 만든 여닫이 프롭 (tools/place-solve.ts). 에디터 목록에서 접어 보여 준다 */
  auto?: boolean;
  needs?: string;
  /**
   * **파기 자리.** 땅과 구분 없는 조작점 — `needs` 의 도구를 들고 있지 않으면
   * 조준 프롬프트가 아예 뜨지 않는다(뜨는 순간 「여기 판다」가 들통난다).
   * 서버는 행동 자체를 막고, 클라는 `파기/다시 덮기` 라고 적는다. (2026-09-18)
   */
  dig?: boolean;
  clue?: string;
  hiddenIn?: string;
  /** 잠겨 있다. LOCKS[id] 에 여는 방법이 있다. */
  lock?: boolean;
  /**
   * **이 단서를 누가 먼저 찾아야** 이 자리가 보인다.
   * 열쇠(`needs`)는 물건을 요구하지만 이건 **아는 것**을 요구한다 —
   * 왜 거기를 뒤져야 하는지 모르면 아무도 안 뒤진다.
   * 결정타에는 쓰지 않는다. 못 찾아도 판이 돌아가는 것에만 건다.
   */
  needsClue?: string;
  /**
   * **이 스위치가 켜져 있어야** 조사가 된다 (2026-09-21, 사흘 v8 — 암실 인화 트레이는
   * 세이프라이트를 켜야 들여다볼 수 있다). 값은 `kind: 'switch'` 프롭의 id —
   * 그 프롭의 `on` 이 true 여야 한다. 클라는 조준을 건너뛰고(어두운 데 프롬프트가
   * 뜨면 「거기 뭔가 있다」를 공짜로 알게 된다), 서버 gate 도 이중으로 막는다.
   */
  needsSwitch?: string;
  /** 조사 뒤 [R] 로 짧은 음원/영상을 재생한다 — 미디어 id (v8 G14 · 07 A11). 서버는 조사 완료 후에만 방 전원에게 play 신호를 뿌리고, 실제 파일은 클라가 /audio/sahol/<id> 에서 연다 */
  play?: string;
  /** 선택형 조사(v8 07 A10): options 중 answer 를 고른 경우에만 clue 카드가 나오고, 나머지는 wrong 문장만 */
  choose?: { prompt: string; options: string[]; answer: string; wrong: string };
  /**
   * **덮개(kind `lid`)** — 카탈로그 조각 하나가 통째로 움직인다. 재받이·파편 위층·상자 플랩·
   * 서랍처럼 「열어야 안이 보이는」 것. 몸통은 PIECES 에 따로 놓고, 이 프롭은 움직이는 부분만이다.
   * **rot 은 조각 규약(+, 다른 프롭의 −rot 과 다르다)** — 몸통 조각과 같은 값을 적는다.
   * `piece` 는 카탈로그 id. 열렸을 때 `slide`(로컬 m, 조각 규약 — 정면 −Z, 앞으로 빠지면 −Z) 만큼 미끄러지거나
   * `swing`(라디안, 로컬 X 축·원점 기준) 만큼 젖힌다. 둘 다 주면 둘 다 한다.
   * 안의 단서는 `hiddenIn` 에 이 id 를 적는다 — 열려야(`open`) 보인다. (2026-09-09)
   */
  piece?: string;
  /**
   * **이미 놓인 조각의 부품 하나를 움직인다** (2026-09-11, Chris: 「서랍이 지금 열리고 닫히는
   * 애가 하나 뿐임」). 서랍장은 서랍이 넷인데 프롭이 하나뿐이라 하나만 열렸다.
   * 조각이 `w.movable` 로 서랍 넷을 안고 있고, 프롭은 **어느 서랍인지만** 말한다 —
   * 프롭마다 조각을 다시 그리지 않으므로 겹치지 않는다.
   *   attach  그 조각의 배치 키(`방/조각#n`)
   *   part    `Piece.openable` 의 부품 이름
   */
  attach?: string;
  part?: string;
  /**
   * **다른 조각의 담는 곳 안에 있다** (2026-09-11). 서랍·상자 속에 넣은 물건.
   * `pos` 는 그 담는 곳 기준의 로컬 좌표고, 조각이 그 부품의 자식이 되어 같이 움직인다.
   *   host  담는 조각의 배치 키(`방/조각#n`)
   *   part  그 조각의 움직이는 부품 이름(없으면 조각 몸통)
   */
  inside?: { host: string; part?: string };
  /** 배치 에디터가 준 크기 배율 */
  scale?: number;
  /** 축별 배율 — 에디터에서 벽·유리벽을 길이만 늘릴 때 쓴다 */
  scale3?: [number, number, number];
  /** 기울기 [x, z] 라디안 */
  tilt?: [number, number];
  slide?: [number, number, number];
  swing?: number;
  /** swing 의 회전축 자리(조각 로컬). 상자 플랩은 뒤 모서리 `[0,0,+d/2]` — 없으면 조각 원점 */
  pivot?: [number, number, number];
  /** needsClue 가 없을 때 보여 줄 말. 세계의 말로 쓴다 */
  blindReason?: string;
  /**
   * 이 라운드부터 조사할 수 있다. 지정 안 하면 처음부터.
   * **공간을 막지 않는다** — 물건 하나가 물리적인 이유로 늦게 열릴 뿐이다.
   */
  fromRound?: number;
  /** 왜 아직 못 보는지. 세계의 말로 쓴다 */
  lateReason?: string;
  /**
   * **열면 모두에게 알리는 한 줄** (사흘 v8 1-d — 봉인봉투를 뜯으면 「봉투 개봉 — 누가」가
   * 공개 로그에 남는다). lid 가 열리는 순간 서버가 `<문구> — <이름>` 을 방 전체에 띄운다.
   * 단서 조사(개인 피드)와 다르다 — 봉인을 깬 사실 자체가 판의 정보다.
   */
  openLog?: string;
  /**
   * **단서가 「나중에 놓이는」 것** (사흘 v8 — 신문실 서랍의 진술 메모). `clue` 가
   * 달려 있어도 `objects[id].stocked` 이 될 때까지는 빈 통이다: 열면 아무 텍스트도
   * 없이 그냥 열리고(횟수도 안 쓴다), `ForensicDef.deliverTo` 가 채운 뒤부터
   * 여는 사람에게 카드가 나온다.
   */
  awaiting?: boolean;
  lightRoom?: string;
  reach?: number;
  /**
   * 조사 대상의 생김새. 지정하지 않으면 데모의 '책 한 권'이 나온다.
   * `marker` 는 아무것도 안 그린다 — 사물함·칠판·난로처럼 가구가 이미 그 자리에
   * 있는 것들이다.
   */
  look?:
    | 'stain'
    | 'crawl'
    | 'drag'
    | 'window'
    | 'papers'
    | 'trays'
    | 'slippers'
    | 'phone'
    | 'tracks'
    /** 언덕길 발자국 두 벌 — 굵은 러그(내리막, 반쯤 메움) + 작은 상·하행 (v8 G12) */
    | 'tracks_pair'
    | 'marker';
}

/**
 * 건물 원형. 「무엇을 지을지」만 적는다 — 삼각형은 client/src/architecture.ts 가
 * 만든다. 계획과 컴파일을 갈라두면 외피를 갈아 끼울 때 매스를 안 건드려도 된다.
 *
 *   school  폐교 본관. 화강석 기단 · 하부 타일 띠 · 긴 처마 · 현관 캐노피
 *   house   고친 빈집. 시멘트 기단 · 급한 슬레이트 지붕 · 문 위 차양
 *   shed    창고. 기단 없음 · 주름 함석 · 외쪽지붕
 */
export type Archetype = 'school' | 'house' | 'shed';

/**
 * 살림집 개성.
 *
 * **집 넷이 전부 같은 모델이었다.** 대로 컷 05(차유리)와 06(명주원)이 좌우
 * 대칭을 빼면 픽셀 단위로 같았다. 추리 게임에서 이건 장식 문제가 아니라
 * 기능 결함이다 — 「누구 집 앞인가」를 화면만 보고 답할 수 없으면 동선 진술을
 * 검증할 수가 없다.
 *
 * 그래서 원형(`house`) 하나를 유지한 채 **밖에서 300m 밖에서도 갈리는 것**
 * 넷만 파라미터로 뺀다: 지붕 재료(색), 물매 각도(실루엣), 굴뚝 종류·위치
 * (수직 요소), 벽 마감(면의 밝기). 평면과 치수는 건드리지 않는다 — 그건
 * 동선 분 수에 영향을 준다.
 */
export interface HouseStyle {
  /** 지붕 재료. 밤 가로등 밑에서 갈리도록 전부 채도를 낮춰 뒀다 */
  roof: 'slate' | 'tinGreen' | 'tinBlue' | 'tinRed' | 'tile';
  /** 물매 각도(도). 19~34 — 이 15도가 원경 실루엣을 가른다 */
  pitchDeg: number;
  /** 굴뚝. 벽돌 굴뚝은 좌/우, `flue` 는 양철 연통, `none` 은 없다 */
  stack: 'brickL' | 'brickR' | 'flue' | 'none';
  /** 벽 마감. block=시멘트블록 줄눈, render=미장 회벽, panel=판벽 */
  wall: 'block' | 'render' | 'panel';
  /** 현관 쪽 툇마루. 가로선이 하나 생겨 집이 앉은 높이가 읽힌다 */
  porch?: boolean;
  /**
   * 창의 불. 안 적으면 기본 규칙(자리 해시로 몇 칸만 켠다)을 따른다.
   * `dark` 는 **한 칸도 안 켠다** — 배정호 집이 그렇다. 브리핑의
   * 「집은 마을 어느 집보다 어둡다」가 문장이 아니라 화면이어야 한다.
   */
  lit?: 'dark' | 'dim';
}

export interface BuildingDef {
  /** 이야기상 **눈이 들어오는** 집. 지붕 밑 눈 마스크에서 뺀다 */
  snowIn?: boolean;
  id: string;
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  archetype: Archetype;
  /**
   * 현관. 캐노피와 계단이 여기 붙는다.
   * `label` 은 문패에 적힐 이름 — 크라임씬 세트가 방마다 이름을 붙여 두는 이유는
   * 밤에 여섯이 12분 안에 흩어져야 하기 때문이다. 누구 집인지 밖에서 읽혀야 한다.
   */
  entrances: { x: number; z: number; along: 'x' | 'z'; label?: string }[];
  /**
   * 이 집이 앉은 바닥 높이. 언덕 위 집은 3.6.
   *
   * **없으면 껍데기가 언덕 속에 묻힌다.** 벽(`WALLS`)은 `baseY` 를 이미 받아
   * y=3.6 에 섰는데 `architecture.ts` 는 이 값을 몰라서 기단·블록줄눈·서까래·
   * 처마·회벽을 전부 y=0 에 깔았다. 그래서 한서진 집이 **두 채**가 됐고,
   * 언덕 밑에서 올려다보면 축대 틈으로 그 묻힌 껍데기가 보였다.
   */
  baseY?: number;
  /** 살림집만. 없으면 기본 슬레이트 집이 된다 */
  style?: HouseStyle;
}

export type RoundMode = 'squad' | 'free' | 'solo';

export interface RoundDef {
  id: number;
  name: string;
  /** 갈 수 있는 구역. 지금은 늘 전부다 — 남겨 둔 것은 나중에 다른 시나리오를 위해서다 */
  zones: readonly string[];
  /** 한 사람이 이 라운드에 쓸 수 있는 조사 횟수 */
  budget: number;
  /**
   * 필름 — 이 라운드에 찍을 수 있는 장수. 찍은 것만 칠판에 걸 수 있다.
   * 지정 안 하면 무제한. **난이도의 첫 손잡이다** — 무제한 86% → 3장 61% (sim/vote.py).
   */
  film?: number;
  /**
   * 이 라운드 뒤 브리핑에서 한 사람이 칠판에 걸 수 있는 장수.
   * 지정 안 하면 서버 기본값(3). 시뮬 권장 — 마지막 브리핑 1장이 검거율을
   * 53%대로 눌러 준다(scenario/사흘/13_수정안 §8, design.md §7의 3·3·2 후속).
   */
  pinLimit?: number;
  minutes: number;
  /** 탐정이 구역을 배정하는 라운드인가 (권고이지 봉쇄가 아니다) */
  assign: boolean;
  /**
   * squad — 한 조씩 나갔다 온다 · free — 전원 동시 · solo — 한 사람씩
   */
  mode: RoundMode;
  /** squad/solo 일 때 한 차례에 주는 시간(분) */
  perTurn: number;
}

/**
 * 잠금 장치 — 코드·열쇠·지문.
 *
 * 결정적인 단서는 그냥 놓여 있지 않다. **여는 방법은 다른 구역에 둔다** — 혼자서는
 * 못 열고 브리핑에서 남이 말해 줘야 한다. 여는 시도는 조사 횟수를 쓰지 않고(틀려도
 * 손해 없음), 열린 뒤 안을 조사할 때 1회가 나간다. 한 번 열린 것은 전원에게 열린 채로 남는다.
 */
export type LockKind = 'code' | 'key' | 'finger';

export interface LockDef {
  kind: LockKind;
  /** 잠긴 상태에서 보이는 문장. 무엇으로 여는지 암시한다 */
  hint: string;
  /** kind:'code' 일 때 자릿수 */
  digits?: number;
  /** kind:'key'|'finger' 일 때 먼저 확보해야 하는 프롭 id */
  needs?: string;
  /** 열지 못했을 때 나오는 말 */
  denied: string;
}

/**
 * **감정서.** 수색으로 안 나오고, 조건 카드(`needsAny` 중 하나)가 **칠판에 오르면**
 * 다음 라운드 시작에 탐정에게 온다 (Chris 2026-09-07: 탐정이 조사해서가 아니라
 * 누가 조사해서 공유해야 온다). `auto` 면 브리핑 상한과 무관하게 그 자리에서 공개된다.
 * `backupRound` 에는 조건 없이 온다 — 부검 없는 판은 판이 아니다.
 */
export interface ForensicDef {
  /** 감정서의 단서 id (secrets 의 CLUES 키) */
  id: string;
  needsAny: readonly string[];
  /**
   * **AND 게이트** (2026-09-21, 사흘 v8 F04 감압지 접합 — 조각과 매장 서류가
   * **둘 다** 칠판에 올라야 접합이 성립한다). 적으면 needsAny 와 별개로
   * 이 목록의 카드가 전부 핀업돼야 하고, 대기 시각은 이쪽 핀 중 가장 늦은
   * pinnedAt 을 쓴다(needsAny 핀들과 함께 max).
   */
  needsAll?: readonly string[];
  /** 이 라운드부터 올 수 있다 */
  round: number;
  auto?: boolean;
  backupRound?: number;
  /**
   * **감정 소요 시간(분).** 라운드 시작에 바로 주면 공짜 배급이다 — 조건 카드가 칠판에
   * 오른 시각과 `round` 시작 시각 중 늦은 쪽에서 이만큼 지나야 온다. 2차(20분)에
   * 5분이면 「2차 끝나기 15분 전」에 도착한다 (Chris 2026-09-07). 없으면 라운드 시작에.
   */
  labMinutes?: number;
  /**
   * **칠판이 아니라 물건에 놓인다** (사흘 v8 — 진술 메모는 수사관이 신문실 서랍에 둔다).
   * 조건이 충족되면 카드가 바로 게시되지 않고 이 프롭의 `stocked` 가 켜진다 —
   * 그 프롭(`awaiting` 단 lid)을 여는 사람이 그제야 카드를 받아 칠판에 핀다.
   * 도착은 공지된다. 감정서처럼 탐정 수령도 아니다 — 놓인 곳까지 가서 가져와야 한다.
   */
  deliverTo?: string;
  /** 로그 머리말. 없으면 「감정」 — 진술 도착은 「진술」로 띄운다 */
  label?: string;
}

/**
 * **시신 부위.** 시신 프롭(`body`)을 조사한 뒤 부위 조사 화면에서 하나씩 누른다.
 * 단서가 없는 부위도 있어야 한다 — 「특이사항 없음」이 곧 정보다(Chris 09-08: 크라임씬처럼).
 * `under` 는 옷을 걷어야 보이는 부위. `clue` 가 있으면 그 단서가 발견된다(수첩·촬영·감정 도장 그대로).
 */
export interface BodyPartDef {
  id: string;
  label: string;
  under?: boolean;
  clue?: string;
}

/**
 * 단서 연결 — 짝이 되는 단서를 **다** 찾으면 추론 카드 한 장이 저절로 열린다.
 * 추론 카드는 새 사실을 주지 않는다. 이미 찾은 것을 나란히 놓았을 때 무엇이 이상한지만 말한다.
 */
export interface LinkDef {
  /** 열리는 추론 카드의 단서 id */
  id: string;
  /** 이걸 전부 찾아야 열린다 */
  needs: readonly string[];
}

/** 카탈로그 기물 배치 — `client/src/furniture/` 의 Piece 를 좌표에 놓는다 */
export interface PieceDef {
  piece: string;
  /** 구역 id. 야외는 'outside' */
  room: string;
  pos: [number, number, number];
  rot?: number;
  dims?: Record<string, number>;
  /**
   * **다른 조각의 담는 곳 안에 있다** (2026-09-11). 서랍·상자 속에 넣은 물건.
   * pos 는 그 담는 곳 기준 로컬 좌표고, 그 부품의 자식이 되어 같이 움직인다.
   */
  inside?: { host: string; part?: string };
  /** 배치 에디터가 준 크기 배율. 없으면 1 (2026-09-10) */
  scale?: number;
  /** 축별 배율 — 에디터에서 벽·유리벽을 길이만 늘릴 때 쓴다 */
  scale3?: [number, number, number];
  /** 기울기 [x, z] 라디안 — 눕히거나 기대 놓은 것 */
  tilt?: [number, number];
  /** 울퉁불퉁한 면 위에 눕는 것(이불·쿠션) — 감사·솔버가 평평함 검사를 뺀다 */
  loose?: boolean;
  /**
   * **내려간 회로 표식** (2026-09-22, 사흘 v8 A6) — 분전반 조각에 「이 회로 차단기만
   * 내려가 있다」를 굽는다. 값은 회로 라벨('L-07') — 조각의 build 가 state 로 받아
   * 번호에 맞는 스위치를 내린다. 없으면 전부 올라간 패널이다.
   */
  tripped?: string;
  /**
   * **이 배치에는 auto lid 를 만들지 않는다** (2026-09-17). 조각에 openable 이 있어도
   * 이 자리만은 여닫이 프롭을 안 단다 — 같은 자리의 프롭(읽는 exam 등)과 조준이
   * 겹쳐, 뚜껑이 단서를 가리는 경우에 쓴다.
   */
  still?: boolean;
}

/**
 * 색 상수가 **여기** 사는 이유.
 *
 * 원래 `level-sahol.ts` 에 있었는데, 배치를 이 파일로 갈라내면서 순환 참조가 됐다
 * (furniture → sahol → furniture). ESM 은 그 고리에서 값을 초기화 순서대로 주지
 * 못해 `Cannot access 'DARKWOOD' before initialization` 으로 죽는다.
 * 값을 아래쪽(이 파일)에 두고 위쪽이 가져다 쓰게 하면 의존이 한 방향이 된다.
 */
export const WOOD = '#6b5540';

export const DARKWOOD = '#42332a';

export const STEEL = '#8f979f';

export const CLOTH = '#4a5568';

export const SCHOOL_GREEN = '#5f7a63';

export const SNOW = '#aebdcd';

/**
 * 가구 라이브러리에서 깎아 만든 물건 한 점을 방에 놓는다.
 *
 * FurnDef 는 색깔 상자다. 벽·책상 골조처럼 「덩어리」면 그것으로 충분하지만,
 * **시대를 말하는 것은 덩어리가 아니라 물건**이다. 폐교를 고쳐 쓰는 마을이라
 * 골조는 1998 년이고, 2026 년은 책상 위에 올라간 것들에서만 나온다.
 */
export interface PieceDef {
  /** client/src/furniture 의 Piece id */
  piece: string;
  room: string;
  /** **바닥 기준**이다. 상판에 올리는 것은 그 상판의 윗면 y 를 넣는다 */
  pos: [number, number, number];
  /** 가구와 같은 규약 — 0 = -Z 를 본다, PI/2 = -X, -PI/2 = +X, PI = +Z */
  rot?: number;
  /**
   * 일부러 규약을 어긴 배치의 이유 — 연통이 벽 속으로 들어가거나 연출로 띄운 것.
   * 있으면 `tools/audit-place.ts` 가 벽 관통·부양을 보고하지 않는다 (MAP_RULES 3 「일부러 띄우는 경우」)
   */
  intent?: string;
  /** 치수 계약 덮어쓰기 (같은 모델을 크기만 달리해 재활용할 때) */
  dims?: Record<string, number>;
  /**
   * **벽에 붙는 물건이다 — 접지 그림자를 깔지 마라.**
   *
   * `world.ts` 는 조각 밑에 접지 그림자를 한 장 깐다. 원점이 바닥 접촉면이라는
   * 규약 덕에 책상 위에 놓은 것도 상판에 제 그림자가 생겨 맞는데, **벽에 붙지만
   * `hangs` 는 아닌 것**이 하나 있다 — 공구 걸이판(`tool_board`)은 원점이 판
   * 밑면이라 `hangs` 를 못 단다(카탈로그 매달림 게이트가 「몸통이 원점 아래」를
   * 요구한다). 그대로 두면 걸이판 높이(1.15m)에 지름 1.2m 짜리 검은 원이
   * 허공에 뜬다 — 그림자가 아니라 얼룩이다.
   */
  wall?: boolean;
  /**
   * **창·문 개구부에 끼는 건축 부재다** (2026-09-17). place-solve 가 창마다 내는
   * `window_slider`·`blind` 에 단다 — 벽면에 반쯤 묻는 게 정상이라 audit-place 의
   * 가구 규칙(벽박힘·받침·창가림·겹침·앞방향)에서 뺀다.
   */
  opening?: boolean;
}
